// Generated Forms V1 (2026-08-13):
//   JSAFormModal          — entry + submit for Job Safety Analysis
//   JSADetailModal        — read-only detail
//   LeakTestFormModal     — entry + submit for Leak Test Report
//   LeakTestDetailModal   — read-only detail
//
// V1 scope: no drafts, no edit-after-submit, no PDF, no work-order link.
// See spec in PRD → Generated Forms V1.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, FileCheck, Trash2 } from 'lucide-react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// 2026-08-19 — JSA free-text fields accept ordinary punctuation. Blocks
// HTML/script tokens, control chars (other than \t\n\r), and emoji/astral
// codepoints. Mirror of backend _JSA_TEXT_DISALLOWED_RE.
const JSA_TEXT_DISALLOWED_RE = /[\x00-\x08\x0b\x0c\x0e-\x1f<>]|[\uD800-\uDBFF]/;
const LETTERS_ONLY_RE = /^[A-Za-z]+$/;
const DIGITS_ONLY_RE = /^[0-9]+$/;

const LEAK_DISCLOSURE =
  'Notice: This Leak Test Report is an internal ORS data-capture tool and is not an official, regulatory, or car-owner-approved leak test record. Official leak test results must be documented on the applicable car-owner-required and approved form.';

// -------- shared helpers --------

const _fmtSubmittedDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const _todayMMDDYYYY = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const _mmddyyyyFromIso = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const _isoFromMmddyyyy = (mmddyyyy) => {
  if (!mmddyyyy || !/^\d{2}\/\d{2}\/\d{4}$/.test(mmddyyyy)) return '';
  const [m, d, y] = mmddyyyy.split('/');
  return `${y}-${m}-${d}`;
};

// =========================================================================
// JSA — entry
// =========================================================================

export function JSAFormModal({ open, onOpenChange, carFileId, onSubmitted }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '', work_description: '',
    cars_associated: '', permits_required: '',
    track_car_protection: '', ppe_requirements: '',
    potential_hazards: '', hazard_mitigation_plan: '',
    job_lead_name: '', review_team_members: '',
  });

  // Discard unsaved data on close.
  useEffect(() => {
    if (!open) {
      setForm({
        title: '', work_description: '',
        cars_associated: '', permits_required: '',
        track_car_protection: '', ppe_requirements: '',
        potential_hazards: '', hazard_mitigation_plan: '',
        job_lead_name: '', review_team_members: '',
      });
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const alnumFields = [
    ['cars_associated', 'Car Number(s) Associated'],
    ['permits_required', 'Permits Required'],
    ['track_car_protection', 'Track & Car Protection'],
    ['ppe_requirements', 'PPE Requirements'],
    ['potential_hazards', 'Potential Hazards'],
    ['hazard_mitigation_plan', 'Hazard Mitigation Plan'],
  ];

  const validate = () => {
    if (!form.title.trim()) return 'Title is required';
    if (!form.work_description.trim()) return 'Work Description is required';
    // 2026-08-19 — normal-text rule: commas/periods/newlines allowed.
    const textFields = [
      ['title', 'Title'],
      ['work_description', 'Work Description'],
      ['cars_associated', 'Car Number(s) Associated'],
      ['permits_required', 'Permits Required'],
      ['track_car_protection', 'Track & Car Protection'],
      ['ppe_requirements', 'PPE Requirements'],
      ['potential_hazards', 'Potential Hazards'],
      ['hazard_mitigation_plan', 'Hazard Mitigation Plan'],
      ['job_lead_name', 'Job Lead'],
    ];
    for (const [k, label] of textFields) {
      const v = (form[k] || '').trim();
      if (!v) return `${label} is required`;
      if (JSA_TEXT_DISALLOWED_RE.test(v)) {
        return `${label} contains characters that are not allowed.`;
      }
    }
    // Review Team Members is optional but still text-safe.
    const rt = (form.review_team_members || '').trim();
    if (rt && JSA_TEXT_DISALLOWED_RE.test(rt)) {
      return 'Review Team Members contains characters that are not allowed.';
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/car-files/${carFileId}/jsa-forms`, form, { withCredentials: true });
      toast.success('JSA submitted');
      onSubmitted?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit JSA');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="jsa-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#005EF8]" />Generate Job Safety Analysis</DialogTitle>
          <DialogDescription>Fields marked with * are required. Once submitted this JSA cannot be edited.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="jsa-title">Title *</Label>
            <Input id="jsa-title" data-testid="jsa-title" value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} placeholder="e.g., Manway Repair — 26 Jul" />
          </div>

          <div>
            <Label htmlFor="jsa-workdesc">Work Description *</Label>
            <Textarea id="jsa-workdesc" data-testid="jsa-work-description" value={form.work_description} onChange={(e) => set('work_description', e.target.value)} rows={3} />
          </div>

          {alnumFields.map(([k, label]) => {
            const isLong = ['track_car_protection', 'ppe_requirements', 'potential_hazards', 'hazard_mitigation_plan'].includes(k);
            const Comp = isLong ? Textarea : Input;
            return (
              <div key={k}>
                <Label htmlFor={`jsa-${k}`}>{label} *</Label>
                <Comp
                  id={`jsa-${k}`}
                  data-testid={`jsa-${k}`}
                  rows={isLong ? 3 : undefined}
                  value={form[k]}
                  onChange={(e) => set(k, e.target.value)}
                />
              </div>
            );
          })}

          <div className="border-t border-[#D3D5D4] pt-4">
            <h4 className="font-semibold mb-2">Review Team</h4>
            <div>
              <Label htmlFor="jsa-job-lead">Job Lead *</Label>
              <Input
                id="jsa-job-lead"
                data-testid="jsa-job-lead"
                value={form.job_lead_name}
                onChange={(e) => set('job_lead_name', e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="mt-3">
              <Label htmlFor="jsa-review-team-members">Review Team Members</Label>
              <Textarea
                id="jsa-review-team-members"
                data-testid="jsa-review-team-members"
                rows={4}
                value={form.review_team_members}
                onChange={(e) => set('review_team_members', e.target.value)}
                maxLength={4000}
                placeholder="One name per line, or comma-separated"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="jsa-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="jsa-submit-btn" className="bg-[#005EF8] hover:bg-[#0047C2]">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// JSA — read-only detail
// =========================================================================

export function JSADetailModal({ open, onOpenChange, form }) {
  if (!form) return null;
  const items = [
    ['Title', form.title],
    ['Work Description', form.work_description],
    ['Car Number(s) Associated', form.cars_associated],
    ['Permits Required', form.permits_required],
    ['Track & Car Protection', form.track_car_protection],
    ['PPE Requirements', form.ppe_requirements],
    ['Potential Hazards', form.potential_hazards],
    ['Hazard Mitigation Plan', form.hazard_mitigation_plan],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="jsa-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#005EF8]" />{form.title || 'JSA'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {items.map(([label, value]) => (
            <div key={label}>
              <div className="text-xs uppercase tracking-wide text-[#5A5A5A]">{label}</div>
              <div className="whitespace-pre-wrap text-[#323033]">{value || '—'}</div>
            </div>
          ))}
          <div className="border-t border-[#D3D5D4] pt-3">
            <div className="text-xs uppercase tracking-wide text-[#5A5A5A]">Job Lead</div>
            <div>{form.job_lead_name || form.review_team?.job_lead_name || '—'}</div>
            {(form.review_team_members || (form.review_team?.crew_members || []).length > 0) && (
              <>
                <div className="text-xs uppercase tracking-wide text-[#5A5A5A] mt-2">Review Team Members</div>
                <div className="whitespace-pre-wrap">
                  {form.review_team_members
                    || (form.review_team?.crew_members || []).map((c) => c.name).join(', ')
                    || '—'}
                </div>
              </>
            )}
          </div>
          <div className="border-t border-[#D3D5D4] pt-3 text-sm text-[#5A5A5A]">
            <div>Submitted By: <span className="text-[#323033] font-medium">{form.submitted_by_name || '—'}</span></div>
            <div>Submitted: <span className="text-[#323033] font-medium">{_fmtSubmittedDate(form.submitted_at)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="jsa-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Leak Test Report — entry
// =========================================================================

export function LeakTestFormModal({ open, onOpenChange, carFileId, carMarkDefault = '', carNumberDefault = '', onSubmitted }) {
  const [submitting, setSubmitting] = useState(false);
  const initial = useMemo(() => ({
    car_mark: (carMarkDefault || '').toUpperCase().slice(0, 4),
    car_number: (carNumberDefault || '').slice(0, 6),
    test_procedure: '', revision_level: '',
    temperature_device: { id_number: '', temperature_range: '', calibration_due_date_iso: '' },
    pressure_gage: { id_number: '', pressure_range: '', calibration_due_date_iso: '' },
    solution_info: { manufacturer: '', solution_type: '', temperature_range: '' },
    surface_temperature_f: '', test_pressure_psig: '',
    test_medium: '', components_tested: '',
    test_result: '', inspector_name: '',
    test_date_iso: new Date().toISOString().slice(0, 10),
  }), [carMarkDefault, carNumberDefault]);
  const [form, setForm] = useState(initial);

  useEffect(() => { if (!open) setForm(initial); }, [open, initial]);

  const set = (path, v) => setForm((f) => {
    const parts = path.split('.');
    if (parts.length === 1) return { ...f, [parts[0]]: v };
    return { ...f, [parts[0]]: { ...f[parts[0]], [parts[1]]: v } };
  });

  const validate = () => {
    if (!form.car_mark || !/^[A-Za-z]{1,4}$/.test(form.car_mark)) return 'Car Mark: 1–4 letters';
    if (!form.car_number || !/^[0-9]{1,6}$/.test(form.car_number)) return 'Car Number: 1–6 digits';
    if (!form.test_procedure.trim()) return 'Test Procedure is required';
    if (!form.revision_level.trim()) return 'Revision Level is required';
    if (!form.temperature_device.id_number.trim()) return 'Temperature Measuring Device ID # is required';
    if (!form.temperature_device.temperature_range.trim()) return 'Temperature Measuring Device Temperature Range is required';
    if (!form.temperature_device.calibration_due_date_iso) return 'Temperature Measuring Device Calibration Due Date is required';
    if (!form.pressure_gage.id_number.trim()) return 'Pressure Gage ID # is required';
    if (!form.pressure_gage.pressure_range.trim()) return 'Pressure Gage Pressure Range is required';
    if (!form.pressure_gage.calibration_due_date_iso) return 'Pressure Gage Calibration Due Date is required';
    if (!form.solution_info.manufacturer.trim()) return 'Leak Test Solution Manufacturer is required';
    if (!form.solution_info.solution_type.trim()) return 'Leak Test Solution Type is required';
    if (!form.solution_info.temperature_range.trim()) return 'Leak Test Solution Temperature Range is required';
    if (form.surface_temperature_f === '' || isNaN(Number(form.surface_temperature_f))) return 'Surface Temperature must be a number';
    if (form.test_pressure_psig === '' || isNaN(Number(form.test_pressure_psig))) return 'Test Pressure must be a number';
    if (!form.test_medium.trim()) return 'Test Medium is required';
    if (!form.components_tested.trim()) return 'Component(s) Tested is required';
    if (!['PASS', 'FAIL'].includes(form.test_result)) return 'Test Result is required';
    if (!form.inspector_name.trim()) return 'Inspector Name is required';
    if (!form.test_date_iso) return 'Date is required';
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        car_mark: form.car_mark.toUpperCase(),
        car_number: form.car_number,
        test_procedure: form.test_procedure.trim(),
        revision_level: form.revision_level.trim(),
        temperature_device: {
          id_number: form.temperature_device.id_number.trim(),
          temperature_range: form.temperature_device.temperature_range.trim(),
          calibration_due_date: _mmddyyyyFromIso(form.temperature_device.calibration_due_date_iso),
        },
        pressure_gage: {
          id_number: form.pressure_gage.id_number.trim(),
          pressure_range: form.pressure_gage.pressure_range.trim(),
          calibration_due_date: _mmddyyyyFromIso(form.pressure_gage.calibration_due_date_iso),
        },
        solution_info: {
          manufacturer: form.solution_info.manufacturer.trim(),
          solution_type: form.solution_info.solution_type.trim(),
          temperature_range: form.solution_info.temperature_range.trim(),
        },
        surface_temperature_f: Number(form.surface_temperature_f),
        test_pressure_psig: Number(form.test_pressure_psig),
        test_medium: form.test_medium.trim(),
        components_tested: form.components_tested.trim(),
        test_result: form.test_result,
        inspector_name: form.inspector_name.trim(),
        test_date: _mmddyyyyFromIso(form.test_date_iso),
      };
      const { data } = await axios.post(`${API}/car-files/${carFileId}/leak-test-reports`, payload, { withCredentials: true });
      toast.success('Leak Test Report submitted');
      onSubmitted?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit Leak Test Report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="leak-test-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileCheck className="w-5 h-5 text-[#005EF8]" />Generate Leak Test Report</DialogTitle>
          <DialogDescription>All fields are required. Once submitted this report cannot be edited.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Car Mark *</Label>
              <Input data-testid="lt-car-mark" value={form.car_mark} maxLength={4}
                onChange={(e) => set('car_mark', e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 4))} />
            </div>
            <div>
              <Label>Car Number *</Label>
              <Input data-testid="lt-car-number" value={form.car_number} maxLength={6} inputMode="numeric"
                onChange={(e) => set('car_number', e.target.value.replace(/\D/g, '').slice(0, 6))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Test Procedure *</Label><Input data-testid="lt-test-procedure" value={form.test_procedure} onChange={(e) => set('test_procedure', e.target.value)} maxLength={200} /></div>
            <div><Label>Revision Level *</Label><Input data-testid="lt-revision-level" value={form.revision_level} onChange={(e) => set('revision_level', e.target.value)} maxLength={100} /></div>
          </div>

          <div className="border-t border-[#D3D5D4] pt-3">
            <h4 className="font-semibold mb-2">Temperature Measuring Device</h4>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>ID # *</Label><Input data-testid="lt-temp-id" value={form.temperature_device.id_number} onChange={(e) => set('temperature_device.id_number', e.target.value)} /></div>
              <div><Label>Temperature Range *</Label><Input data-testid="lt-temp-range" value={form.temperature_device.temperature_range} onChange={(e) => set('temperature_device.temperature_range', e.target.value)} /></div>
              <div><Label>Calibration Due Date *</Label><Input type="date" data-testid="lt-temp-due" value={form.temperature_device.calibration_due_date_iso} onChange={(e) => set('temperature_device.calibration_due_date_iso', e.target.value)} /></div>
            </div>
          </div>

          <div className="border-t border-[#D3D5D4] pt-3">
            <h4 className="font-semibold mb-2">Pressure Gage Information</h4>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>ID # *</Label><Input data-testid="lt-press-id" value={form.pressure_gage.id_number} onChange={(e) => set('pressure_gage.id_number', e.target.value)} /></div>
              <div><Label>Pressure Range *</Label><Input data-testid="lt-press-range" value={form.pressure_gage.pressure_range} onChange={(e) => set('pressure_gage.pressure_range', e.target.value)} /></div>
              <div><Label>Calibration Due Date *</Label><Input type="date" data-testid="lt-press-due" value={form.pressure_gage.calibration_due_date_iso} onChange={(e) => set('pressure_gage.calibration_due_date_iso', e.target.value)} /></div>
            </div>
          </div>

          <div className="border-t border-[#D3D5D4] pt-3">
            <h4 className="font-semibold mb-2">Leak Test Solution Information</h4>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Manufacturer *</Label><Input data-testid="lt-sol-mfr" value={form.solution_info.manufacturer} onChange={(e) => set('solution_info.manufacturer', e.target.value)} /></div>
              <div><Label>Solution Type *</Label><Input data-testid="lt-sol-type" value={form.solution_info.solution_type} onChange={(e) => set('solution_info.solution_type', e.target.value)} /></div>
              <div><Label>Temperature Range *</Label><Input data-testid="lt-sol-range" value={form.solution_info.temperature_range} onChange={(e) => set('solution_info.temperature_range', e.target.value)} /></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-[#D3D5D4] pt-3">
            <div><Label>Surface Temperature (°F) *</Label><Input data-testid="lt-surface-temp" type="number" step="0.1" value={form.surface_temperature_f} onChange={(e) => set('surface_temperature_f', e.target.value)} /></div>
            <div><Label>Test Pressure (psig) *</Label><Input data-testid="lt-test-pressure" type="number" step="0.1" value={form.test_pressure_psig} onChange={(e) => set('test_pressure_psig', e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Test Medium *</Label><Input data-testid="lt-medium" value={form.test_medium} onChange={(e) => set('test_medium', e.target.value)} /></div>
            <div>
              <Label>Test Result *</Label>
              <Select value={form.test_result} onValueChange={(v) => set('test_result', v)}>
                <SelectTrigger data-testid="lt-result"><SelectValue placeholder="Select result" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PASS" data-testid="lt-result-pass">Pass</SelectItem>
                  <SelectItem value="FAIL" data-testid="lt-result-fail">Fail</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Component(s) Tested *</Label>
            <Textarea data-testid="lt-components" rows={3} value={form.components_tested} onChange={(e) => set('components_tested', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Inspector Name *</Label><Input data-testid="lt-inspector" value={form.inspector_name} onChange={(e) => set('inspector_name', e.target.value)} /></div>
            <div><Label>Date *</Label><Input type="date" data-testid="lt-date" value={form.test_date_iso} onChange={(e) => set('test_date_iso', e.target.value)} /></div>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="lt-disclosure">
            <strong>Notice:</strong> {LEAK_DISCLOSURE.replace('Notice: ', '')}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="lt-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="lt-submit-btn" className="bg-[#005EF8] hover:bg-[#0047C2]">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Leak Test Report — read-only detail
// =========================================================================

export function LeakTestDetailModal({ open, onOpenChange, form }) {
  if (!form) return null;
  const items = [
    ['Car Mark', form.car_mark],
    ['Car Number', form.car_number],
    ['Test Procedure', form.test_procedure],
    ['Revision Level', form.revision_level],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lt-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileCheck className="w-5 h-5 text-[#005EF8]" />Leak Test Report — {form.car_mark} {form.car_number} — {form.test_date}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {items.map(([label, value]) => (
              <div key={label}>
                <div className="text-xs uppercase tracking-wide text-[#5A5A5A]">{label}</div>
                <div className="text-[#323033]">{value || '—'}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#D3D5D4] pt-2">
            <h4 className="font-semibold">Temperature Measuring Device</h4>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <div><div className="text-xs text-[#5A5A5A]">ID #</div><div>{form.temperature_device?.id_number || '—'}</div></div>
              <div><div className="text-xs text-[#5A5A5A]">Temperature Range</div><div>{form.temperature_device?.temperature_range || '—'}</div></div>
              <div><div className="text-xs text-[#5A5A5A]">Calibration Due</div><div>{form.temperature_device?.calibration_due_date || '—'}</div></div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold">Pressure Gage Information</h4>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <div><div className="text-xs text-[#5A5A5A]">ID #</div><div>{form.pressure_gage?.id_number || '—'}</div></div>
              <div><div className="text-xs text-[#5A5A5A]">Pressure Range</div><div>{form.pressure_gage?.pressure_range || '—'}</div></div>
              <div><div className="text-xs text-[#5A5A5A]">Calibration Due</div><div>{form.pressure_gage?.calibration_due_date || '—'}</div></div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold">Leak Test Solution Information</h4>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <div><div className="text-xs text-[#5A5A5A]">Manufacturer</div><div>{form.solution_info?.manufacturer || '—'}</div></div>
              <div><div className="text-xs text-[#5A5A5A]">Solution Type</div><div>{form.solution_info?.solution_type || '—'}</div></div>
              <div><div className="text-xs text-[#5A5A5A]">Temperature Range</div><div>{form.solution_info?.temperature_range || '—'}</div></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 border-t border-[#D3D5D4] pt-2">
            <div><div className="text-xs text-[#5A5A5A]">Surface Temperature (°F)</div><div>{form.surface_temperature_f}</div></div>
            <div><div className="text-xs text-[#5A5A5A]">Test Pressure (psig)</div><div>{form.test_pressure_psig}</div></div>
            <div><div className="text-xs text-[#5A5A5A]">Test Medium</div><div>{form.test_medium}</div></div>
            <div>
              <div className="text-xs text-[#5A5A5A]">Test Result</div>
              <div className={form.test_result === 'PASS' ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>{form.test_result}</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#5A5A5A]">Component(s) Tested</div>
            <div className="whitespace-pre-wrap text-[#323033]">{form.components_tested || '—'}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-xs text-[#5A5A5A]">Inspector</div><div>{form.inspector_name}</div></div>
            <div><div className="text-xs text-[#5A5A5A]">Date</div><div>{form.test_date}</div></div>
          </div>
          <div className="border-t border-[#D3D5D4] pt-2 text-sm text-[#5A5A5A]">
            <div>Submitted By: <span className="text-[#323033] font-medium">{form.submitted_by_name || '—'}</span></div>
            <div>Submitted: <span className="text-[#323033] font-medium">{_fmtSubmittedDate(form.submitted_at)}</span></div>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="lt-detail-disclosure">
            <strong>Notice:</strong> {LEAK_DISCLOSURE.replace('Notice: ', '')}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lt-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Row renderer for the generated-forms list (shared)
// =========================================================================

export function GeneratedFormRow({ formTypeLabel, title, submittedBy, submittedAt, onView, canDelete, onDelete, testIdPrefix = 'genform', resultBadge = null }) {
  return (
    <div className="flex items-center justify-between border border-[#D3D5D4] rounded-md p-3 bg-white" data-testid={`${testIdPrefix}-row`}>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-[#005EF8] font-semibold">{formTypeLabel}</div>
        <div className="font-medium text-[#323033] truncate flex items-center gap-2">
          <span className="truncate">{title}</span>
          {resultBadge}
        </div>
        <div className="text-xs text-[#5A5A5A] mt-0.5">
          Submitted by {submittedBy || '—'} • {_fmtSubmittedDate(submittedAt)}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="outline" size="sm" onClick={onView} data-testid={`${testIdPrefix}-view-btn`}>View</Button>
        {canDelete && (
          <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:border-red-300" data-testid={`${testIdPrefix}-delete-btn`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function LeakTestResultBadge({ result }) {
  const isPass = (result || '').toUpperCase() === 'PASS';
  const isFail = (result || '').toUpperCase() === 'FAIL';
  const cls = isPass
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : (isFail ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-50 text-gray-700 border-gray-200');
  return (
    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${cls}`} data-testid="lt-result-badge">
      {result || '—'}
    </span>
  );
}

export { LEAK_DISCLOSURE };
